/**
 * useBeziehungsnetz — TanStack Query hook for the Kunde-Struktur ego graph (Story UI-U8).
 *
 * live → GET /beziehungsnetz/:subjectType/:id via the typed client.
 * mock → the synthetic fixture (VITE_DATA_MODE unset in dev/tests).
 *
 * `retry: false` so an ABSENT endpoint fails fast — the Struktur tab feature-detects
 * `isError` and falls back to the flat useBeziehungenForKunde feed (Liste only).
 *
 * [Source: apps/web/src/features/entity-detail/hooks/useEntityDetail.ts#useBeziehungenForKunde]
 */

import { useQuery } from '@tanstack/react-query';
import type { BeziehungsnetzDto } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { mockBeziehungsnetz } from '../components/struktur/mock';

export type BeziehungsnetzSubjectType = 'person' | 'firma';

export function useBeziehungsnetz(id: string, subjectType?: BeziehungsnetzSubjectType) {
  const live = isLiveData();
  return useQuery<BeziehungsnetzDto>({
    queryKey: qk.beziehungsnetz(subjectType ?? 'unknown', id),
    queryFn: async () => {
      if (!live) return mockBeziehungsnetz(id);
      if (!subjectType) throw new Error('subjectType required in live mode');
      return apiClient.beziehungsnetz.get(subjectType, id);
    },
    enabled: !live || Boolean(subjectType),
    retry: false,
    ...(live ? {} : { initialData: () => mockBeziehungsnetz(id), staleTime: Infinity }),
  });
}
